import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api.service';
import { AudioType } from '@app/preferences/models/audio-type.enum';
import {
  TTSLanguagesDto,
  TTSVoice,
  VoicemailGreetingAudioItem,
  VoicemailSettingsModel,
  VoicemailTTSGreeting,
} from '@app/preferences/models/voicemail.model';
import { environment } from '@environment/environment';
import { Observable } from 'rxjs';

@Injectable()
export class VoicemailSettingsApiService extends ApiService {
  constructor() {
    super(environment.gateway);
  }

  getVoicemailSettings(): Observable<VoicemailSettingsModel> {
    return this.get<VoicemailSettingsModel>(`users/{me}/voicemail/settings`);
  }

  saveVoicemailSettings(settings: VoicemailSettingsModel): Observable<{ success: boolean }> {
    return this.put<{ success: boolean }>(`users/{me}/voicemail/settings`, settings);
  }

  updatePin(pin: string): Observable<{ success: boolean }> {
    const body = {
      pin,
    };
    return this.post<{ success: boolean }>(`users/{me}/update-pin`, body);
  }

  getTTSLanguages(): Observable<TTSLanguagesDto> {
    return this.get<TTSLanguagesDto>(`users/{me}/tts/languages`);
  }

  getTTSVoices(languageId: string): Observable<TTSVoice[]> {
    return this.get<TTSVoice[]>(`users/{me}/tts/voices/${languageId}`);
  }

  uploadTTS(greeting: VoicemailTTSGreeting, index: number | string, audioType: AudioType): Observable<{ url: string }> {
    return this.post<{ url: string }>(`users/{me}/audio/${audioType}/${index}/upload`, greeting);
  }

  previewTTS(voice: TTSVoice, text: string): Observable<Blob> {
    return this.post(`users/{me}/tts/preview`, { text: text, voice: voice }, { responseType: 'blob' });
  }

  uploadAudio(
    name: string,
    description: string,
    index: number | string,
    audioType: AudioType,
    format: string
  ): Observable<{ url: string }> {
    return this.post<{ url: string }>(`users/{me}/audio/${audioType}/${index}/upload`, {
      description,
      name,
      format: `audio/${format}`,
    });
  }

  uploadFileGreeting(blobfile: Blob, url: string): Observable<unknown> {
    return this.put<unknown>(url, blobfile);
  }

  getListOfGreetings(): Observable<VoicemailGreetingAudioItem[]> {
    return this.get<VoicemailGreetingAudioItem[]>(`users/{me}/audio/greeting/list`);
  }

  getListOfRecordedNames(): Observable<VoicemailGreetingAudioItem[]> {
    return this.get<VoicemailGreetingAudioItem[]>(`users/{me}/audio/name/list`);
  }

  deleteAudioFile(audioType: AudioType, index: string): Observable<unknown> {
    return this.delete(`users/{me}/audio/${audioType}/${index}`);
  }

  downloadAudioFile(index: string, type: AudioType): Observable<Blob> {
    return this.get<Blob>(`users/{me}/audio/${type}/${index}/download`, {
      responseType: 'blob',
    });
  }
}
